import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, Loader2, MapPin, ArrowRight, Phone } from "lucide-react";
import {
  API,
  BUSINESS,
  ROOF_OPTIONS,
  HOUSE_OPTIONS,
} from "@/lib/config";

function SelectCard({ option, active, onClick, testid }) {
  return (
    <button
      type="button"
      data-active={active}
      data-testid={testid}
      onClick={onClick}
      className="bid-select-card text-left w-full"
    >
      <div className="relative">
        <img
          src={option.image}
          alt={option.label}
          className="w-full h-40 md:h-44 object-cover"
        />
        {active && (
          <div className="absolute top-3 right-3 w-8 h-8 bg-[#002FA7] text-white flex items-center justify-center">
            <Check size={16} strokeWidth={3} />
          </div>
        )}
      </div>
      <div className="p-5 border-t border-slate-200">
        <div className="font-display font-bold text-lg uppercase tracking-tight">
          {option.label}
        </div>
        <div className="text-sm text-slate-600 mt-1">{option.description}</div>
      </div>
    </button>
  );
}

export default function QuoteCalculator() {
  const [roof, setRoof] = useState("shingle");
  const [house, setHouse] = useState("one_story");
  const [zip, setZip] = useState("");
  const [loading, setLoading] = useState(false);
  const [quote, setQuote] = useState(null);
  const debounceRef = useRef(null);

  const valid = useMemo(() => /^\d{5}$/.test(zip), [zip]);

  useEffect(() => {
    if (!valid) {
      setQuote(null);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        setLoading(true);
        const res = await axios.post(`${API}/quote/calculate`, {
          roof_type: roof,
          house_type: house,
          zip_code: zip,
        });
        setQuote(res.data);
      } catch (e) {
        toast.error("Could not calculate your quote. Please try again.");
      } finally {
        setLoading(false);
      }
    }, 350);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [roof, house, zip, valid]);

  return (
    <section
      id="calculator"
      data-testid="quote-calculator-section"
      className="bg-slate-50 border-b border-slate-200"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-12">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-5">
              <span className="bid-divider" />
              <span className="bid-eyebrow">Live Quote Tool</span>
            </div>
            <h2 className="font-display font-bold text-4xl sm:text-5xl tracking-tight leading-tight">
              Build your quote
              <br />
              in three quick steps.
            </h2>
          </div>
          <div className="lg:col-span-5 flex items-end">
            <p className="text-base text-slate-700 leading-relaxed">
              No hidden fees, no phone tag. Pick your roof, your home type, and
              enter your zip — your price updates in real time.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Left: steps */}
          <div className="lg:col-span-8 space-y-12">
            {/* Step 1 */}
            <div data-testid="calculator-step-roof">
              <StepHeader number="01" title="Choose your roof type" />
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {ROOF_OPTIONS.map((opt) => (
                  <SelectCard
                    key={opt.id}
                    option={opt}
                    active={roof === opt.id}
                    onClick={() => setRoof(opt.id)}
                    testid={`roof-option-${opt.id}`}
                  />
                ))}
              </div>
            </div>

            {/* Step 2 */}
            <div data-testid="calculator-step-house">
              <StepHeader number="02" title="Choose your home type" />
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                {HOUSE_OPTIONS.map((opt) => (
                  <SelectCard
                    key={opt.id}
                    option={opt}
                    active={house === opt.id}
                    onClick={() => setHouse(opt.id)}
                    testid={`house-option-${opt.id}`}
                  />
                ))}
              </div>
            </div>

            {/* Step 3 */}
            <div data-testid="calculator-step-zip">
              <StepHeader number="03" title="Enter your zip code" />
              <div className="flex flex-col sm:flex-row items-stretch gap-4 max-w-md">
                <div className="relative flex-1">
                  <MapPin
                    size={18}
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
                  />
                  <input
                    inputMode="numeric"
                    pattern="\d{5}"
                    maxLength={5}
                    value={zip}
                    onChange={(e) =>
                      setZip(e.target.value.replace(/\D/g, "").slice(0, 5))
                    }
                    placeholder="e.g. 32801"
                    data-testid="zipcode-input"
                    className="w-full pl-11 pr-4 py-4 border-2 border-slate-300 bg-white font-mono text-lg tracking-widest focus:outline-none focus:border-[#002FA7]"
                  />
                </div>
                {valid && quote && !quote.in_service_area && (
                  <div className="text-xs uppercase tracking-widest font-bold text-[#FF2A2A] self-center">
                    Outside service area
                  </div>
                )}
              </div>
              {zip && !valid && (
                <p className="mt-2 text-sm text-[#FF2A2A]">
                  Enter a 5-digit US zip code.
                </p>
              )}
            </div>
          </div>

          {/* Right: live price */}
          <div className="lg:col-span-4">
            <div
              className="sticky top-24 border-2 border-slate-900 bg-white p-8 md:p-10 bid-noise"
              data-testid="live-price-card"
            >
              <div className="bid-eyebrow mb-2">Your live quote</div>
              <div className="flex items-end gap-3 mb-2">
                {loading ? (
                  <div className="font-display text-5xl font-black flex items-center gap-3">
                    <Loader2
                      className="animate-spin text-slate-400"
                      size={36}
                    />
                  </div>
                ) : (
                  <div
                    data-testid="live-price-display"
                    className="font-display text-6xl font-black tracking-tighter"
                  >
                    {quote ? `$${quote.total_price}` : "$—"}
                  </div>
                )}
                <span className="text-slate-500 mb-2 text-sm uppercase tracking-widest font-bold">
                  Total
                </span>
              </div>
              <div className="h-px bg-slate-200 my-6" />

              <ul className="space-y-3 text-sm">
                <Row label={houseLabel(house)} value={priceOr(quote, "base_price")} />
                <Row
                  label={`Roof: ${roofLabel(roof)}`}
                  value={priceOr(quote, "roof_surcharge", true)}
                />
                <Row
                  label={
                    quote?.tier_label
                      ? `Travel · ${quote.tier_label}`
                      : "Travel fee"
                  }
                  value={priceOr(quote, "travel_fee", true)}
                />
              </ul>

              {quote && (
                <div className="mt-6 text-xs text-slate-500 leading-relaxed">
                  {quote.message}
                </div>
              )}

              <a
                href={`tel:${BUSINESS.phoneRaw}`}
                className="bid-btn-danger w-full justify-center mt-8"
                data-testid="quote-book-now-btn"
              >
                <Phone size={16} />
                Call to book this slot
              </a>
              <a
                href="#contact"
                className="block text-center mt-4 text-sm font-bold tracking-widest uppercase text-[#002FA7] hover:underline"
                data-testid="quote-request-callback"
              >
                or request a callback <ArrowRight size={14} className="inline" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function StepHeader({ number, title }) {
  return (
    <div className="flex items-center gap-4 mb-6">
      <div className="font-display font-black text-2xl text-[#002FA7]">
        {number}
      </div>
      <div className="h-px flex-1 bg-slate-300" />
      <div className="font-display font-bold uppercase tracking-tight text-xl">
        {title}
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <li className="flex items-center justify-between">
      <span className="text-slate-600">{label}</span>
      <span className="font-mono font-bold text-slate-900">{value}</span>
    </li>
  );
}

function priceOr(quote, key, addPrefix) {
  if (!quote) return "—";
  const v = quote[key];
  if (addPrefix) {
    if (v === 0) return "+ $0";
    if (v < 0) return `− $${Math.abs(v)}`;
    return `+ $${v}`;
  }
  return `$${v}`;
}

function houseLabel(id) {
  return HOUSE_OPTIONS.find((h) => h.id === id)?.label || id;
}
function roofLabel(id) {
  return ROOF_OPTIONS.find((r) => r.id === id)?.label || id;
}
